export interface OptionMediaValue {
  source: 'option';
  id: string;
  name: string;
  preview: string | undefined;
  url: string | undefined;
}

export interface ProjectMediaValue {
  source: 'project';
  id: string | undefined;
  name: string | undefined;
  key: string | undefined;
  url: string | undefined;
}

export type MediaValue = OptionMediaValue | ProjectMediaValue;

interface TemplateOption {
  name: string;
  value?: string | number;
  preview?: string;
}

interface FieldSpec {
  value?: string;
  options?: Array<string | TemplateOption>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RawValue = string | Record<string, any> | null | undefined;

// Build OptionMediaValue from either raw string or already-normalized value
export function buildOptionMediaValue(spec: FieldSpec, rawVal: RawValue): OptionMediaValue | null {
  const options = spec.options || [];
  if (!options.length) return null;

  // Already normalized (draft / UI)
  if (rawVal && typeof rawVal === 'object' && (rawVal as OptionMediaValue).source === 'option') {
    return rawVal as OptionMediaValue;
  }

  // Backend value: string
  if (rawVal && typeof rawVal === 'string') {
    const name = extractOptionNameFromValue(rawVal, options);
    if (!name) return null;
    const opt = options.find((o) => typeof o === 'object' && o.name === name) as TemplateOption | undefined;
    if (!opt) return null;

    return {
      source: 'option',
      id: opt.name,
      name: opt.name,
      preview: opt.preview,
      url: opt.preview,
    };
  }

  // Fallback to template default
  const name = extractOptionNameFromValue(spec.value, options);
  if (!name) return null;
  const opt = options.find((o) => typeof o === 'object' && o.name === name) as TemplateOption | undefined;
  if (!opt) return null;

  return {
    source: 'option',
    id: opt.name,
    name: opt.name,
    preview: opt.preview,
    url: opt.preview,
  };
}

function extractOptionNameFromValue(
  val: string | undefined,
  options: Array<string | TemplateOption>,
): string | null {
  if (!val || typeof val !== 'string') return null;
  // If options are strings
  if (options.length > 0 && typeof options[0] === 'string') {
    if ((options as string[]).includes(val)) return val;
    return null;
  }
  // If options are objects
  const opt = (options as TemplateOption[]).find((o) => o.name === val || String(o.value) === val);
  return opt ? opt.name : null;
}

// Build ProjectMediaValue from either:
// - our normalized object
// - job field object like { value, key, ... }
// - or nothing
export function buildProjectMediaValue(rawVal: RawValue): ProjectMediaValue | null {
  if (!rawVal) return null;

  // Already normalized
  if (typeof rawVal === 'object' && (rawVal as ProjectMediaValue).source === 'project') {
    return rawVal as ProjectMediaValue;
  }

  // From job backend, assuming shape: { value, key, id? }
  if (rawVal && typeof rawVal === 'object' && ('value' in rawVal || 'set_value' in rawVal)) {
    const name = (rawVal as Record<string, string>).value || (rawVal as Record<string, string>).set_value;

    return {
      source: 'project',
      id: (rawVal as Record<string, string>).id,
      name,
      key: (rawVal as Record<string, string>).key,
      url: (rawVal as Record<string, string>).url,
    };
  }

  // Template defaults for project media we can ignore for now
  return null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface JobLike {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fields?: Record<string, Record<string, any>>;
}

// Get "raw value" from selectedJob.fields which can be of two shapes:
// - job from API: fields[cat][fname] = { value, type, ... }
// - draft-backed "job": fields[cat][fname] = FormValue directly
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getJobRawValue(selectedJob: JobLike | null | undefined, cat: string, fname: string): any {
  const field = selectedJob?.fields?.[cat]?.[fname];
  if (!field) return undefined;

  // Drafts: we store FormValue directly
  if (typeof field === 'string' || (typeof field === 'object' && field?.source)) {
    return field;
  }

  // API jobs: if it's option-media or text/number, raw string is fine
  if (typeof field === 'object') {
    // keep whole object for project media (option=false) so we don't lose url/key/id
    if (field.type && ['image', 'audio', 'video'].includes(field.type) && field.option === false) {
      return field;
    }

    if ('value' in field) return field.value;
    if ('set_value' in field) return field.set_value;
  }

  return field;
}
